<template>
  <div
    :class="[
      `panel_${id}`,
      currentView,
      { active: isActive(id) },
      { hidden: !options.visible },
      { childrenOfActive },
      { carouselItem },
    ]"
    :style="style"
    v-indicator="{ id, setActive }"
  >
    <div v-html="`<style>${options.style.css}</style>`"></div>

    <div v-if="hasNoWidget" class="information">
      <PanelWidgetOptions :panel-id="id" />
    </div>
    <div @click.stop="infoClick" class="information" v-else>
      {{ info }}
      <i class="fas fa-hand-pointer" v-if="!isActive(id)"></i>
      <i class="fas fa-level-up-alt" v-if="!childrenOfActive"></i>
    </div>

    <i class="fas fa-plus-circle plus-icon" v-if="hasNoWidget"></i>
    <div :class="['container', { carousel: options.carousel }]" ref="container" :style="containerStyle">
      <Panel
        v-for="panel in panels"
        :key="panel"
        :id="panel"
        :carouselItem="options.carousel"
        :childrenOfActive="isActive(id)"
      />
      <Widget v-if="widget" :id="widget" />
    </div>

    <template v-if="options.carousel">
      <i
        class="fas fa-chevron-left fa-2x carousel-button backward"
        @mouseover="carouselHoverLeft = true"
        @mouseleave="carouselHoverLeft = false"
        :style="{ color: carouselHoverLeft ? options.carouselArrowColorHover : options.carouselArrowColor }"
        @click="carouselMove(-1)"
      ></i>
      <i
        class="fas fa-chevron-right fa-2x carousel-button forward"
        @mouseover="carouselHoverRight = true"
        @mouseleave="carouselHoverRight = false"
        :style="{ color: carouselHoverRight ? options.carouselArrowColorHover : options.carouselArrowColor }"
        @click="carouselMove(1)"
      ></i>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Mixin from '@/components/page-editor/Mixin.js'
import Widget from '@/components/page-editor/Widget'
import PanelWidgetOptions from '@/components/page-editor/PanelWidgetOptions'

import smoothScroll from '@/helpers/smoothScrollHelper'

export default {
  name: 'Panel',
  mixins: [Mixin],
  components: {
    Widget,
    PanelWidgetOptions,
  },
  props: {
    childrenOfActive: {
      type: Boolean,
      default: false,
    },
    carouselItem: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('landing', ['panelCopied']),
    panels() {
      return this.current.children.filter(child => child.startsWith('P-'))
    },
    widget() {
      return this.current.children.find(child => child.startsWith('W-'))
    },
    hasNoWidget() {
      return !this.widget && !this.panels.length
    },
    style() {
      const widthPct = `${(100 * this.options.size) / 12}%`
      const marginLeftRight = this.options.margin.right + this.options.margin.left

      return {
        backgroundImage: this.getBackgroundImageOrder,
        order: this.options.position,
        alignSelf: this.options.selfAlign.vertical,
        justifySelf: this.options.selfAlign.horizontal,
        width: `calc(${widthPct} - ${marginLeftRight}px)`,
        marginTop: `${this.options.margin.top}px`,
        marginRight: `${this.options.margin.right}px`,
        marginBottom: `${this.options.margin.bottom}px`,
        marginLeft: `${this.options.margin.left}px`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    },
    getBackgroundImageOrder() {
      if (this.options.background.isImagePriority) {
        return `url(${this.options.background.url}),
        linear-gradient(
          ${this.options.background.color},
          ${this.options.background.color}
        )`
      }
      return `
        linear-gradient(
          ${this.options.background.color},
          ${this.options.background.color}
        ), url(${this.options.background.url})`
    },
    containerStyle() {
      return {
        alignItems: this.options.align.vertical,
        justifyContent: this.options.align.horizontal,
        paddingTop: `${this.options.padding.top}px`,
        paddingRight: `${this.options.padding.right}px`,
        paddingBottom: `${this.options.padding.bottom}px`,
        paddingLeft: `${this.options.padding.left}px`,
      }
    },
    info() {
      const panels = this.panels.length
      const info = panels
        ? `${panels} ${this.$i18n.t('components.page-editor.panel.panel/panels')}`
        : !this.widget
        ? this.$i18n.t('components.page-editor.panel.empty')
        : ''
      return `${this.current.title}${info && ` - ${info}`}`
    },
  },
  data() {
    return {
      carouselHoverLeft: false,
      carouselHoverRight: false,
    }
  },
  methods: {
    carouselMove(to) {
      const container = this.$refs.container
      let toScroll = (container.scrollLeft + container.clientWidth * to) % container.scrollWidth
      if (toScroll < 0) {
        toScroll = container.scrollWidth - toScroll
      }
      smoothScroll(container, 'scrollLeft', toScroll, 100)
    },
    infoClick() {
      this.setActive(!this.isActive(this.id) ? this.id : this.parentId)
    },
  },
}
</script>

<style lang="scss" scoped>
[class^='panel_'] {
  display: flex;
  flex-wrap: wrap;
  position: relative;

  .button {
    position: absolute;
    background: white;
    left: 4px;
    top: 4px;
    border: 1px solid #ddd;
    border-radius: 5px;
    outline: none;
    cursor: pointer;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 16px;
  }

  > .information {
    position: absolute;
    display: none;
    top: -25px;
    right: 0px;
    justify-content: center;
    align-items: center;
    padding: 0 10px;
    background: var(--indicator-active);
    z-index: 5;
    cursor: pointer;
  }

  &.hidden {
    > .information {
      z-index: 1;
    }
  }

  > .container {
    display: flex;
    flex-wrap: wrap;
    width: 100%;

    &.carousel {
      flex-wrap: nowrap;
      overflow-x: hidden;
      justify-content: flex-start !important;

      > [class^='panel_'] {
        flex: 0 0 auto;
      }
      ~ .carousel-button {
        position: absolute;
        top: 45%;
        cursor: pointer;
        &.backward {
          left: 0;
        }
        &.forward {
          right: 0;
        }
      }
    }

    &:empty {
      height: 100px;
      border: 2px dashed rgb(199, 199, 199);
      margin: 10px;
    }
  }

  .plus-icon {
    top: 45%;
    position: absolute;
    left: 48%;
    color: rgb(199, 199, 199);
  }

  &.active {
    border: 2px dashed var(--indicator-active);
  }

  &.childrenOfActive {
    box-shadow: 0 0 0 1px var(--indicator-children), inset 0 0 0 1px var(--indicator-children);
    &:hover {
      > .information {
        background: var(--indicator-children);
        z-index: 2;
        top: 0;
      }
      > .container > [class^='panel_'] {
        box-shadow: 0 0 0 0 var(--indicator-grandchildren), inset 0 0 0 1px var(--indicator-grandchildren);
      }
    }
  }

  &.active,
  &.childrenOfActive:hover {
    > .information {
      display: flex;
    }
  }

  &.carouselItem {
    .information {
      top: 0 !important;
    }
    &.active,
    .active {
      > .information {
        left: 0;
        right: unset;
      }
    }
  }
}

.remove-widget-icon {
  margin-left: 8px;
}
</style>
